import React, {
  ReactElement,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Row } from 'react-table';
import { Flex, Text } from 'rebass/styled-components';

import useAccountsColumns from 'containers/AccountsList/useAccountsColumns';

import { Table, SearchInput } from 'ui/patterns';
import { Button, Stack } from 'ui/components';

import { useGetAccounts } from 'hooks';

import * as Icon from 'assets/icons';

import { Account } from 'types/accounts';
import { AccountsPanelProps } from './copySection';

const sortItem = { sortKey: 'CID', sortOrder: 'asc' };
const userSettings = ['CID', 'SID', 'PID', 'Account Name', 'Status'];
const columnSearch = {
  Status: 'Active',
};
function useAccountsStates(showPanel: boolean) {
  const [accountKeywordSearch, setAccountKeywordSearch] = useState(''); // keyword search
  const [columnsSearch, setColumnsSearch] = useState<Record<string, string>>({
    ...columnSearch,
  }); // header search
  const [currentSortItem, setCurrentSortItem] = useState({ ...sortItem });

  const columns = useAccountsColumns(userSettings, true);
  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  const {
    data: accountList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useGetAccounts(
    'Live',
    accountKeywordSearch,
    columnsSearch,
    currentSortItem,
  );

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  const tableAccountsData = useMemo(() => {
    if (accountList) {
      return accountList.pages.map((dataItem: any) => dataItem?.Data).flat();
    }
  }, [accountList]);

  const totalAccountsItems = useMemo(() => {
    if (accountList) {
      return accountList.pages[0]?.FilteredRecords;
    }
    return 0;
  }, [accountList]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch, setColumnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const handleAccountKeywordSearch = (value: string): void => {
    setAccountKeywordSearch(value);
  };

  const handleReset = (): void => {
    setAccountKeywordSearch('');
    setColumnsSearch({
      ...columnSearch,
    });
    setCurrentSortItem(sortItem);
  };

  const onEndReached = () => {
    if (tableAccountsData) {
      if (hasNextPage && tableAccountsData?.length < totalAccountsItems)
        fetchNextPage();
    }
  };

  useEffect(() => {
    handleReset();
  }, [showPanel]);

  return {
    handleReset,
    isFetching,
    totalAccountsItems,
    accountKeywordSearch,
    handleAccountKeywordSearch,
    accountsTableProps: {
      columns,
      data: tableAccountsData,
      isLoading: isLoading,
      hasColumnSelection: false,
      headerUpdateCallBack: headerUpdateCallBack,
      handleSortChange: handleSortChange,
      sortedItem: currentSortItem,
      columnsSearch: columnsSearch,
      userSettings: userSettings,
      onEndReached: onEndReached,
      isFetching: !!isFetchingNextPage,
    },
  };
}

export default function AccountsPanel({
  selectedAccountId,
  setSelectedAccountId,
  isHidden,
  showPanel,
}: AccountsPanelProps): ReactElement {
  const {
    handleReset,
    isFetching,
    totalAccountsItems,
    accountKeywordSearch,
    handleAccountKeywordSearch,
    accountsTableProps,
  } = useAccountsStates(showPanel);

  const onAccountsRowClick = (row: Row<Account>) => {
    setSelectedAccountId(row?.original?.Id);
  };

  if (isHidden) return <></>;

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Text fontSize={1} color="text.body">
          {accountsTableProps.data && accountsTableProps.data.length > 0 && (
            <Text mb={2}>{`Showing ${
              accountsTableProps.data.length
            } of ${totalAccountsItems}${
              isFetching ? ' | Processing' : ''
            }`}</Text>
          )}
        </Text>
        <Stack direction="x" gap={2}>
          <SearchInput
            value={accountKeywordSearch}
            handleChange={handleAccountKeywordSearch}
            placeholder="Search Accounts"
          />
          <Button
            onClick={handleReset}
            iconBefore={<Icon.Refresh title="" ariaHidden="true" />}
            variant="secondary"
          >
            Reset All
          </Button>
        </Stack>
      </Flex>

      <Table
        selectedRowId={selectedAccountId}
        onRowClick={onAccountsRowClick}
        {...accountsTableProps}
        height="450px"
        noOptions
      />
    </>
  );
}
